import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { Dashboard } from './pages/Dashboard';
import { LeagueTable } from './pages/LeagueTable';
import { Heatmap } from './pages/Heatmap';
import { Alerts } from './pages/Alerts';
import { NbsExtract } from './pages/NbsExtract';
import { EmptyState } from './ui/components';
import { Shell, type NavItem } from './ui/Shell';

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // c14-analytics' kpisRouter gates every KPI/heatmap/alerts route on the
  // same requireRoles('EXEC_VIEWER', 'ANALYST') — the two roles see an
  // identical surface (see server/personas.ts for the full citation).
  // NBS_PARTNER is disjoint: 403 on all of the above, the only role allowed
  // to call POST /jobs/nbs-extract, and it only ever gets that endpoint's
  // aggregated output. Nav + routes below mirror that split exactly, so a
  // persona never sees a link the API would 403.
  const canReadDashboards = persona.roles.includes('EXEC_VIEWER') || persona.roles.includes('ANALYST');
  const canExtract = persona.roles.includes('NBS_PARTNER');

  const items: NavItem[] = [
    ...(canReadDashboards
      ? [
          { to: '/dashboard', label: 'Cabinet dashboard', icon: '◫' },
          { to: '/league-table', label: 'MDA league table', icon: '☰' },
          { to: '/heatmap', label: 'District heatmap', icon: '▦' },
          { to: '/alerts', label: 'SLA alerts', icon: '⚑' },
        ]
      : []),
    ...(canExtract ? [{ to: '/nbs-extract', label: 'NBS extract', icon: '⇩' }] : []),
  ];

  const landing = canReadDashboards ? '/dashboard' : canExtract ? '/nbs-extract' : '/no-access';

  return (
    <Routes>
      <Route path="/" element={<Shell title="Analytics & Command Centre" items={items} />}>
        <Route index element={<Navigate to={landing} replace />} />
        {canReadDashboards && <Route path="dashboard" element={<Dashboard />} />}
        {canReadDashboards && <Route path="league-table" element={<LeagueTable />} />}
        {canReadDashboards && <Route path="heatmap" element={<Heatmap />} />}
        {canReadDashboards && <Route path="alerts" element={<Alerts />} />}
        {canExtract && <Route path="nbs-extract" element={<NbsExtract />} />}
        <Route
          path="no-access"
          element={
            <EmptyState
              title="No dashboard access yet"
              hint="Ask a Cabinet/VP office administrator to grant EXEC_VIEWER, ANALYST or NBS_PARTNER."
            />
          }
        />
        <Route path="*" element={<Navigate to={landing} replace />} />
      </Route>
    </Routes>
  );
}
